import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BulkMessageRecord } from '../../../../services/analyticsService';
import { format } from 'date-fns';

interface BulkMessageAnalyticsProps {
    data: BulkMessageRecord[];
    dailyData: Array<{ date: string; dispatches: number; messagesSent: number; messagesFailed: number }>;
}

const BulkMessageAnalytics: React.FC<BulkMessageAnalyticsProps> = ({ data, dailyData }) => {
    const totalSent = data.reduce((sum, d) => sum + d.messages_sent, 0);
    const totalFailed = data.reduce((sum, d) => sum + d.messages_failed, 0);
    const successRate = totalSent + totalFailed > 0 ? ((totalSent / (totalSent + totalFailed)) * 100).toFixed(1) : '0';

    const chartData = dailyData.map(d => ({
        date: format(new Date(d.date), 'dd/MM'),
        Enviadas: d.messagesSent,
        Falhadas: d.messagesFailed
    }));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                📨 Análise de Disparos em Massa
            </h3>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Mensagens Enviadas</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{totalSent.toLocaleString()}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Mensagens Falhadas</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{totalFailed.toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Taxa de Sucesso</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{successRate}%</p>
                </div>
            </div>

            {/* Chart */}
            {chartData.length > 0 ? (
                <div className="mb-6">
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="date" stroke="#9CA3AF" />
                            <YAxis stroke="#9CA3AF" />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1F2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px'
                                }}
                            />
                            <Legend />
                            <Bar dataKey="Enviadas" fill="#10B981" />
                            <Bar dataKey="Falhadas" fill="#EF4444" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    Nenhum dado disponível para o período selecionado
                </div>
            )}

            {/* Recent Campaigns Table */}
            <div className="overflow-x-auto">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Campanhas Recentes</h4>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Destinatários
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Enviadas
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Falhadas
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Taxa de Sucesso
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Data
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {data.slice(0, 10).map((campaign) => {
                            const rate = campaign.messages_sent + campaign.messages_failed > 0
                                ? ((campaign.messages_sent / (campaign.messages_sent + campaign.messages_failed)) * 100).toFixed(1)
                                : '0';

                            return (
                                <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {campaign.total_recipients}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400 font-medium">
                                        {campaign.messages_sent}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-red-600 dark:text-red-400 font-medium">
                                        {campaign.messages_failed}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {rate}%
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${campaign.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                                                campaign.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                                                    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                            }`}>
                                            {campaign.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                        {format(new Date(campaign.created_at), 'dd/MM/yyyy HH:mm')}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {data.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        Nenhuma campanha encontrada
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkMessageAnalytics;
